import { ReactElement, FC, useState, useEffect, useRef, memo, useCallback } from 'react'

import Benefits from '../../components/about/aboutBenefits'
import Countries from '../../components/about/aboutCountries'
import ProductOfferings from '../../components/about/aboutProductOfferings'
import School from '../../components/about/aboutSchools'
import StudentAndFaculty from '../../components/about/aboutStudentAndFaculty'
import Image from '../../components/atoms/images/image'
import Page from '../../components/page'
import Parallax from '../../components/parallax'
import { ASSETS, ID_PAGE_MAIN_ELEMENT } from '../../constants'
import { useI18n } from '../../hooks'

import './about.css'

const aboutHeaderBackground = `${ASSETS}/banners/about-us/about_us_desktop.jpg`
const aboutPartnerBackground = `${ASSETS}/pages/about/partner.png`
const aboutByKivutoLeftImage = `${ASSETS}/pages/about/5-kvt-bottom-1.jpg`
const aboutByKivutoRightImage = `${ASSETS}/pages/about/5-kvt-bottom-2.png`

const PartnerWithUs: FC = (): ReactElement => {
  const { t } = useI18n()
  return (
    <div
      style={{
        backgroundImage: `url(${aboutPartnerBackground})`,
      }}
      className="about-partner"
    >
      <div className="about-partner-overlay">
        <div className="about-partner-text">
          <h1 className="heavy">{t('About_partnerWithUs')}</h1>
          <p>{t('About_joinOurPartnersText')}</p>
          <p className="about-partner-action">
            {t('Common_click')}{' '}
            <a className="heavy" href="/sell-with-us">
              {t('Common_here')}
            </a>{' '}
            {t('Common_toApply').toLowerCase()}.
          </p>
        </div>
      </div>
    </div>
  )
}

const ByKivuto: FC = (): ReactElement => {
  const { t } = useI18n()
  return (
    <div className="about-by-kivuto">
      <div className="about-by-kivuto-text">
        <h1 className="heavy">{t('About_poweredByKivuto')}</h1>
        <p>{t('About_poweredByKivutoText')}</p>
        <button
          className="roman"
          onClick={() => {
            window.open('https://kivuto.com/')
          }}
        >
          {t('About_visitKivuto')}
        </button>
      </div>
      <div className="about-by-kivuto-img">
        <Image
          className="about-kivuto-botton-img"
          src={aboutByKivutoLeftImage}
          alt="about page kivuto image"
          size={{ width: '40%', height: 'auto' }}
          disableLazy={true}
        />
        <Image
          disableLazy={true}
          className="about-kivuto-botton-img"
          src={aboutByKivutoRightImage}
          alt="about page kivuto image"
          size={{ width: '40%', height: 'auto' }}
        />
        <div className="about-by-kivuto-img-overlay"></div>
      </div>
    </div>
  )
}

const AboutPage: FC = (): ReactElement => {
  const studentAndFacultyRef = useRef<HTMLElement>(null)
  const schoolRef = useRef<HTMLElement>(null)
  const productOfferingRef = useRef<HTMLElement>(null)
  const countriesRef = useRef<HTMLElement>(null)
  const benefitsRef = useRef<HTMLElement>(null)

  const [showStudentAndFaculty, setShowStudentAndFaculty] = useState(false)
  const [showSchool, setShowSchool] = useState(false)
  const [showProductOffering, setShowProductOffering] = useState(false)
  const [showCountries, setShowCountries] = useState(false)
  const [showBenefits, setShowBenefits] = useState(false)

  const getIfSectionNeedShow = (currentElement: HTMLElement, scrollTop: number, viewHight: number): boolean => {
    const topPosition = currentElement.offsetTop
    const triggerHight = topPosition + currentElement.offsetHeight * 0.5
    return viewHight + scrollTop > triggerHight
  }

  const handleScroll = useCallback(
    (ev: Event): void => {
      const targetElement = ev.target as HTMLElement
      const scrollTop = targetElement?.scrollTop
      const viewHight = window.innerHeight

      if (studentAndFacultyRef?.current && !showStudentAndFaculty) {
        const isShow = getIfSectionNeedShow(studentAndFacultyRef.current, scrollTop, viewHight)
        if (isShow) {
          setShowStudentAndFaculty(true)
        }
      }

      if (schoolRef?.current && !showSchool) {
        const isShow = getIfSectionNeedShow(schoolRef.current, scrollTop, viewHight)
        if (isShow) {
          setShowSchool(true)
        }
      }

      if (productOfferingRef?.current && !showProductOffering) {
        const isShow = getIfSectionNeedShow(productOfferingRef.current, scrollTop, viewHight)
        if (isShow) {
          setShowProductOffering(true)
        }
      }

      if (countriesRef?.current && !showCountries) {
        const isShow = getIfSectionNeedShow(countriesRef.current, scrollTop, viewHight)
        if (isShow) {
          setShowCountries(true)
        }
      }

      if (benefitsRef?.current && !showBenefits) {
        const isShow = getIfSectionNeedShow(benefitsRef.current, scrollTop, viewHight)
        if (isShow) {
          setShowBenefits(true)
        }
      }
    },
    [showBenefits, showCountries, showProductOffering, showSchool, showStudentAndFaculty]
  )
  useEffect(() => {
    const page = document.getElementById(ID_PAGE_MAIN_ELEMENT)
    if (page) {
      page.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (page) {
        page.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll])
  const { t } = useI18n()
  return (
    <Page metaData={{ title: t('About_meta_title'), description: t('About_meta_description'), charset: 'utf-8' }} className="about-page">
      <Parallax
        classNames={{ banner: 'about-parallax-container-custom', img: 'parallax-img-custom' }}
        bgPosition="center"
        imgData={{ src: aboutHeaderBackground, alt: 'about-banner' }}
        isStatic={true}
      >
        <div className="about-header">
          <h1 className="heavy">{t('About_aboutOnthehub')}</h1>
          <p>{t('About_aboutOnThehubText')}</p>
          <p>{t('About_aboutOnThehubText2')}</p>
          <i className="feather-arrow-down-circle"></i>
        </div>
      </Parallax>
      <section ref={studentAndFacultyRef} className="about-page-sections">
        {showStudentAndFaculty ? <StudentAndFaculty /> : null}
      </section>
      <section ref={schoolRef} className="about-page-sections">
        {showSchool ? <School /> : null}
      </section>
      <section ref={productOfferingRef} className="about-page-sections">
        {showProductOffering ? <ProductOfferings /> : null}
      </section>
      <section ref={countriesRef} className="about-page-sections">
        {showCountries ? <Countries /> : null}
      </section>
      <section ref={benefitsRef} className="about-page-sections">
        {showBenefits ? <Benefits /> : null}
      </section>
      <section className="about-page-sections about-page-partner">
        <PartnerWithUs />
      </section>
      <section className="about-page-by-kivuto">
        <ByKivuto />
      </section>
    </Page>
  )
}

export default memo(AboutPage)
